<template>
  <div class="product-page-price-wrapper">
    <div class="prices">
      <div 
        v-if="discountPrice"
        class="current-price"
        :class="{ 'discount-price': displayRedPrice }"
      >
        {{ formattedDiscountPrice }}
      </div>

      <div 
        v-if="regularPrice && !discountPrice"
        class="current-price"
      >
        {{ formattedRegularPrice }}
      </div>

      <span 
        :class="{ 'vat-text': true, 'price-new': discountPrice && displayRedPrice  }"
      > 
        {{ showVat ?  $translate("INCL_VAT") : $translate("EXCL_VAT") }}
      </span>
      
      <div 
        v-if="regularPrice && discountPrice && displayRedPrice"
        :class="{ 'has-discount': discountPrice }"
      >
        {{ formattedRegularPrice }}
      </div>
    </div>

    <div v-if="discountPrice && displayRedPrice">
      <span class="lowest-price-thirty-days">
        <span>
          {{ $translate('LOWEST_PRICE_DURING_PREVIOUS_30_DAYS') }}
          {{ formattedRegularPrice }}
        </span>
        <span
          v-if="discountPercentage"
          class="discount-percentage"
        >
          {{ discountPercentage }}
        </span>
        <tooltip
          :text="$translate('LOWEST_PRICE_DURING_PREVIOUS_30_DAYS_TOOLTIP')"
          class="info-icon"
        >
          <InfoIcon :aria-label="$translate('LOWEST_PRICE_DURING_PREVIOUS_30_DAYS_TOOLTIP')" />
        </tooltip>  
      </span>
    </div>

    <div class="comparison-price">
      <span>
        {{ $translate('C_PRICE_PER') }} {{ comparisonFactor }} {{ unit }}: {{ formattedComparisonPrice }}
      </span>
    </div>
  </div>
</template>

<script>
import { onMounted, onUnmounted, computed } from 'vue';
import InfoIcon from '@/assets/icons/information-blue-rounded.svg?component';
import { useProductPrice } from '@/composables/product-price.js';
import { useCurrencyFormatter } from '@/composables/use-currency-formatter.js';

export default {
  props: {
    showVat: {
      type: Boolean,
      required: true
    },
    updateOnChange: {
      type: Boolean,
      required: false
    },
    productData: {
      type: Object,
      required: true
    },
    loggedIn: {
      type: Boolean,
      required: true
    },
    handlePriceLogUpdate: {
      type: Boolean,
      required: false,
      default: false
    },
    currency: {
      type: String,
      required: false,
      default: 'SEK'
    }
  },
  components: {
    InfoIcon
  },
  setup(props) {
    const {
      intialPriceLoaded,
      price,
      handleUpdatedProductConfiguratorPrice,
      getPrice,
      discountPrice,
      regularPrice,
      discountPercentage,
      vatText,
      displayRedPrice,
      comparisonPrice,
      priceRaw,
      fetchErpPrice
    } = useProductPrice(props.productData, props);

    const { formatPrice } = useCurrencyFormatter(props.currency);

    const formattedDiscountPrice = computed(() => {
      return discountPrice.value != null ? formatPrice(discountPrice.value) : null;
    });

    const formattedRegularPrice = computed(() => {
      return regularPrice.value != null ? formatPrice(regularPrice.value) : null;
    });

    const formattedComparisonPrice = computed(() => {
      return comparisonPrice.value != null ? formatPrice(comparisonPrice.value) : null;
    });

    const lowestPrice30Days = computed(() => {
      if (props.priceLogPrice) {
        return props.priceLogPrice;
      }
      return price.value;
    });

    const unit = computed(() => {
      return props.productData.unit || '';
    });

    const comparisonFactor = computed(() => {
      return props.productData.comparison_factor || 1;
    });

    onMounted(async () => {
      if (props.updateOnChange) {
        await getPrice();
        const productForm = document.querySelector('form[name=add_product]');
        if (productForm) {
          productForm.addEventListener('input', getPrice);
        }
      }

      if (props.loggedIn) {
        fetchErpPrice(props.productData.id);
      }

      document.addEventListener(
        'vendreProductPageProductConfiguratorPriceUpdated',
        handleUpdatedProductConfiguratorPrice
      );

      intialPriceLoaded.value = true;
    });

    onUnmounted(() => {
      document.removeEventListener(
        'vendreProductPageProductConfiguratorPriceUpdated',
        handleUpdatedProductConfiguratorPrice
      );
    });

    return {
      InfoIcon,
      vatText,
      discountPrice,
      regularPrice,
      discountPercentage,
      displayRedPrice,
      comparisonPrice,
      comparisonFactor,
      unit,
      lowestPrice30Days,
      formattedDiscountPrice,
      formattedRegularPrice,
      formattedComparisonPrice
    };
  }
};
</script>
